import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components'
import _ from 'lodash';
import SocialIcon from './social-icon';

const emailBase = 'mailto:';
const facebookBase = 'https://www.facebook.com/sharer/sharer.php';
const linkedinBase = 'https://www.linkedin.com/shareArticle';
const pinterestBase = 'http://pinterest.com/pin/create/button/';
const tumblrBase = 'http://www.tumblr.com/share/link';
const twitterBase = 'https://twitter.com/intent/tweet';

// Simple helper to convert key-values into an url-safe query string, dropping
// any keys that have no value.
function queryString(map) {
  const encodedParams = _.chain(map)
    .omitBy(_.isEmpty)
    .map((value, key) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
    .value();

  return `?${encodedParams}`;
}

/**
 * A Glowforge-standard social media site icon that can be clicked to launch a
 * popup prompting the user to share some content to that social site.
 * Depending on the site, different properties will be used or ignored. The
 * most commonly used properties are `url`, `hashtag`, and `text`. This
 * component will evolve to match Glowforge's standard for how share prompts
 * work on each site, as well as changes to each site's API, so clients should
 * pass data for as many props as possible to take advantage of new
 * functionality with future releases.
 *
 * @param site - which social site this button is for, e.g. 'facebook',
 *  'twitter'
 * @param url - the url of the content being shared e.g. 'https://glowforge.com/akkika'
 * @param hashtag - do not include the # character e.g. 'glowforge'
 * @param text - the primary short-text to use as default content for
 *  the user's post
 * @param quote - a quote from Glowforge to include alongside the link iteslf
 * @param image - an image to share (only currently supported by pinterest)
 * @param props.size - passed on to SocialIcon - standard sizes small, medium,
 *  or large
 * @param props.appearance - passed on to SocialIcon - available styles square,
 *  round, clear
 * @returns {*}
 * @constructor
 */

class ShareButton extends React.PureComponent {
  static currentLocation() {
    if (typeof window === 'undefined') {
      return null;
    }
    return _.get(window, ['location', 'href']);
  }

  showInWindow = (event) => {
    const { site } = this.props;
    event.preventDefault();
    window.open(this.shareUrl(), `${site}-share-popup`, 'width=640,height=400');
  };

  shareUrl() {
    const {
      site,
      url,
      hashtag,
      text,
      quote,
      image,
    } = this.props;

    switch (site) {
      case 'email':
        return emailBase + queryString({ subject: text, body: url });
      case 'facebook':
        return facebookBase + queryString({ u: url, hashtag: `#${hashtag}`, quote });
      case 'linkedin':
        return linkedinBase + queryString({
          mini: 'true',
          url,
          summary: text,
          source: 'Glowforge',
        });
      case 'pinterest':
        return pinterestBase + queryString({
          do: 'buttonPin',
          media: image,
          url,
          description: text,
        });
      case 'tumblr':
        return tumblrBase + queryString({ url });
      case 'twitter':
        return twitterBase + queryString({ url, text, hashtags: hashtag });
      default:
        return null;
    }
  }

  render() {
    const { className } = this.props;
    return (
      <div className={`ShareButton ${className}`}>
        <a
          href={this.shareUrl()}
          target='_blank'
          rel='noopener noreferrer'
          onClick={this.showInWindow}>
          <SocialIcon {...this.props} />
        </a>
      </div>
    );
  }
}

ShareButton.propTypes = {
  ...SocialIcon.propTypes,
  className: PropTypes.string,
  url: PropTypes.string,
  hashtag: PropTypes.string,
  text: PropTypes.string,
  quote: PropTypes.string,
  image: PropTypes.string,
  // Override `site` from `SocialIcon` to limit to the sites supported here.
  site: PropTypes.oneOf([
    'email',
    'facebook',
    'linkedin',
    'pinterest',
    'tumblr',
    'twitter',
  ]).isRequired,
}

ShareButton.defaultProps = {
  className: '',
  url: ShareButton.currentLocation(),
  hashtag: 'glowforge',
  text: null,
  quote: null,
  image: null,
}

const { propTypes: shareButtonPropTypes } = ShareButton;

export {
  shareButtonPropTypes,
}

export default styled(ShareButton)`
  display: inline-block;
`;